import React, {
    useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { motion } from 'framer-motion';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import {
    PhoneInput,
    CountryIso2,
} from 'react-international-phone';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { ReservationEventPayloadStandard } from '@r6digital/rapidstor-types/types/Events/Reservation';
import { ShowPricesPayload } from '@r6digital/rapidstor-types/types/Events/ShowPrices';
import {
    BaseReservation,
    RapidStorReservationRequest,
    ReservationPatchRequest,
} from '@r6digital/rapidstor-types/types/Reservation';

import ChevronLeft from '@/assets/icon-chevron-left.svg?react';
import IconMailSent from '@/assets/icon-mail-sent.svg?react';
import IconCalendar from '@/assets/icon-calendar.svg?react';
import {
    usePatchReservationMutation,
    usePostContactMutation,
    usePostSaveQuoteMutation,
} from '@/core/api/rapidstor';
import { useExternalData } from '@/core/hooks/useExternalData';
import Logger from '@/core/utils/logging';
import getOrCreateSessionId from '@/core/utils/session';
import {
    descriptorDimensions,
    getDatePickerRange,
    getDefaultNeededDate,
    isFreeReservation as isFreeReservationCheck,
    payLaterButtonText,
    saveQuoteButtonText,
    showPricesTitleText,
    toNeededDate,
} from '@/core/utils/rapidstor';

import store from '@/store';
import {
    selectContactCard,
    selectContactData,
    selectReservationData,
    setContactCard,
    setContactData,
    setReservationData,
} from '@/store/bookingSlice';

import {
    ANIMATION_SPEED,
    contactVariants,
} from '@/features/rapidstor/animations';
import {
    reservationEvent,
    saveQuoteEvent,
    showPricesEvent,
} from '@/features/rapidstor/PostMessageEvents';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-international-phone/style.css';
import classes from './styles.module.scss';

import Button from '../button';
import Spinner from '../spinner';
import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';
import useDefaultCountry from '@/core/hooks/useDefaultCountry';

import {
    EMAIL_PILL_DOMAINS,
    applyEmailDomain,
    isValidTld,
    shouldShowEmailPills,
} from '@/core/utils/email';
import { monthlyToWeekly } from '@/core/utils/currency';
import { isSafeUrl } from '@/core/utils/url';
import { validateEmail } from '@/core/utils/validation';

export type FormType =
    | 'quote'
    | 'reservation'
    | 'showPrices'
    | 'contactMe'
    | 'largerUnit';

interface Props {
    location: Location;
}

export default function ContactCard({ location }: Props) {
    const [emailQuote] = usePostSaveQuoteMutation();
    const [patchReservation] = usePatchReservationMutation();
    const [postContact] = usePostContactMutation();
    const navigate = useNavigate();
    const { handleMakeReservation, persona } = useContext(JourneyContext);
    const { generateExternalData } = useExternalData();
    const contactData = useSelector(selectContactData);
    const contactCardState = useSelector(selectContactCard);
    const reservationData = useSelector(selectReservationData);
    const [cookies, setCookie] = useCookies(['contactData', 'sessionId']);
    const { trackEvent } = useRapidStorTracking();
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { t } = useTranslation();

    /**
   * UI Animation State
   */
    const [formLoading, setFormLoading] = useState<boolean>(false);

    const dateRange = useMemo(
        () => getDatePickerRange(location, 'movein'),
        [location],
    );
    const nowDate = useMemo(
        () => moment(
            contactData?.neededDate
                || cookies?.contactData?.neededDate
                || new Date(),
        ),
        [contactData, cookies],
    );

    const [neededDate, setNeededDate] = useState<Date>(
        getDefaultNeededDate(location, nowDate),
    );

    // This sessionId is provided as external data when submitting the enquiry or
    // reservation to RapidStor. This allows external systems to link the lead and
    // reservation since RapidStor doesn't have a nice way of doing this natively.
    useEffect(() => {
        getOrCreateSessionId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const defaultCountry = useDefaultCountry(location);

    /**
   * Contact Form State
   *
   * Prefills using contactData from state if available
   */
    const [form, updateForm] = useImmer<Record<string, string>>({
        location: location.locationCode,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    });
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [emailTldError, setEmailTldError] = useState(false);
    const [requiredFieldsError, setRequiredFieldsError] = useState(false);

    const showEmailPills = useMemo(
        () => shouldShowEmailPills(form.email),
        [form.email],
    );

    const handleEmailChange = useCallback((e: React.BaseSyntheticEvent) => {
        const val = String(e.target.value);
        updateForm((draft) => {
            draft.email = val;
            return draft;
        });
        if (val.includes('@') && val.split('@')[1]?.includes('.')) {
            setEmailTldError(!isValidTld(val));
        } else {
            setEmailTldError(false);
        }
    }, [updateForm]);

    const handlePillClick = useCallback((domain: string) => {
        const fullEmail = applyEmailDomain(form.email, domain);
        updateForm((draft) => {
            draft.email = fullEmail;
            return draft;
        });
        setEmailTldError(!isValidTld(fullEmail));
    }, [form.email, updateForm]);

    const update = (key: string, event: React.BaseSyntheticEvent) => {
        updateForm((draft) => {
            draft[key] = String(event.target.value);
            return draft;
        });
    };

    useEffect(() => {
        if (contactData) {
            updateForm((draft) => {
                draft.firstName = contactData.firstName || '';
                draft.lastName = contactData.lastName || '';
                draft.email = contactData.email || '';
                draft.phone = contactData.phone || '';
            });
        }
    }, [contactData, updateForm]);

    // Each CTA/detail string below is computed in a memo — a function boundary
    // neither build plugin can see through — so alongside the text we declare how
    // the inspector should classify it: `i18nKey` for translatable copy (emerald
    // "click to translate") or `setting` for a RapidStor setting helper (amber
    // "controlled by settings"). Exactly one is set per state; the render sites
    // pass them as data-i18n-key / data-rs-setting-dynamic.
    const CTA_TITLE = useMemo((): {
        text: string;
        i18nKey?: string;
        setting?: string;
    } => {
        switch (contactCardState.type) {
            case 'showPrices':
                return {
                    text: showPricesTitleText(location),
                    setting: 'showPricesTitleText',
                };
            case 'reservation':
                return {
                    text: payLaterButtonText(location),
                    setting: 'payLaterButtonText',
                };
            case 'quote':
                return {
                    text: saveQuoteButtonText(location),
                    setting: 'saveQuoteButtonText',
                };
            case 'largerUnit':
                return {
                    text: t('contact_card:largerUnit:title'),
                    i18nKey: 'contact_card.largerUnit.title',
                };
            case 'contactMe':
            default:
                return {
                    text: t('contact_card:contactMe:title'),
                    i18nKey: 'contact_card.contactMe.title',
                };
        }
    }, [contactCardState.type, location, t]);

    const CTA_BUTTON = useMemo((): {
        text: string;
        i18nKey?: string;
        setting?: string;
    } => {
        switch (contactCardState.type) {
            case 'showPrices':
                return {
                    text: t('contact_card:showPrices:submit_text'),
                    i18nKey: 'contact_card.showPrices.submit_text',
                };
            case 'reservation':
                return {
                    text: payLaterButtonText(location),
                    setting: 'payLaterButtonText',
                };
            case 'quote':
                return {
                    text: saveQuoteButtonText(location),
                    setting: 'saveQuoteButtonText',
                };
            case 'largerUnit':
                return {
                    text: t('contact_card:largerUnit:submit_text'),
                    i18nKey: 'contact_card.largerUnit.submit_text',
                };
            case 'contactMe':
            default:
                return {
                    text: t('contact_card:contactMe:submit_text'),
                    i18nKey: 'contact_card.contactMe.submit_text',
                };
        }
    }, [contactCardState.type, location, t]);

    const detail1 = useMemo((): { text: string; i18nKey: string } => {
        switch (contactCardState.type) {
            case 'showPrices':
                return {
                    text: t('contact_card:showPrices:form_title'),
                    i18nKey: 'contact_card.showPrices.form_title',
                };
            case 'reservation':
                return {
                    text: t('contact_card:reservation:title'),
                    i18nKey: 'contact_card.reservation.title',
                };
            case 'largerUnit':
                return {
                    text: t('contact_card:largerUnit:form_title'),
                    i18nKey: 'contact_card.largerUnit.form_title',
                };
            case 'quote':
            default:
                return {
                    text: t('contact_card:quote:title'),
                    i18nKey: 'contact_card.quote.title',
                };
        }
    }, [contactCardState.type, t]);

    const detail2 = useMemo((): { text: string; i18nKey: string } => {
        switch (contactCardState.type) {
            case 'showPrices':
                return {
                    text: t('contact_card:showPrices:subtitle'),
                    i18nKey: 'contact_card.showPrices.subtitle',
                };
            case 'reservation':
                return {
                    text: t('contact_card:reservation:subtitle'),
                    i18nKey: 'contact_card.reservation.subtitle',
                };
            case 'largerUnit':
                return {
                    text: t('contact_card:largerUnit:subtitle'),
                    i18nKey: 'contact_card.largerUnit.subtitle',
                };
            case 'quote':
            default:
                return {
                    text: t('contact_card:quote:subtitle'),
                    i18nKey: 'contact_card.quote.subtitle',
                };
        }
    }, [contactCardState.type, t]);

    const success1 = useMemo((): { text: string; i18nKey: string } => {
        switch (contactCardState.type) {
            case 'reservation':
                return {
                    text: t('contact_card:reservation:success'),
                    i18nKey: 'contact_card.reservation.success',
                };
            case 'largerUnit':
                return {
                    text: t('contact_card:largerUnit:success'),
                    i18nKey: 'contact_card.largerUnit.success',
                };
            case 'quote':
            default:
                return {
                    text: t('contact_card:quote:success'),
                    i18nKey: 'contact_card.quote.success',
                };
        }
    }, [contactCardState.type, t]);

    const success2 = useMemo((): { text: string; i18nKey: string } => {
        switch (contactCardState.type) {
            case 'reservation':
                return {
                    text: t('contact_card:reservation:success_detail'),
                    i18nKey: 'contact_card.reservation.success_detail',
                };
            case 'largerUnit':
                return {
                    text: t('contact_card:largerUnit:success_detail'),
                    i18nKey: 'contact_card.largerUnit.success_detail',
                };
            case 'quote':
            default:
                return {
                    text: t('contact_card:quote:success_detail'),
                    i18nKey: 'contact_card.quote.success_detail',
                };
        }
    }, [contactCardState.type, t]);

    const patchExistingReservation = async () => {
        if (!reservationData?.token) {
            return null;
        }

        const body: ReservationPatchRequest = {
            token: reservationData.token,
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            payload: {
                tenant: {
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                },
                reservation: {
                    neededDate: toNeededDate(neededDate),
                    enquiry: true,
                    deal: contactCardState.deal || reservationData.deal || null,
                },
                cart: {},
                charges: {},
            },
        };

        const externalData = generateExternalData();
        if (externalData) {
            body.payload.reservation.externalData = externalData;
        }

        return patchReservation(body).unwrap();
    };

    const sendReservationToRapidstor = async ({
        enquiry = false,
        isShowPrices = false,
        reserveButton,
    }: {
        enquiry?: boolean;
        isShowPrices?: boolean;
        reserveButton?: 'PayNow' | 'PayLater';
    } = {}) => {
        if (!contactCardState.unitType) {
            Logger.info('[sendReservationToRapidstor] Missing unitType', location);
            return undefined;
        }

        // `neededDate` is a required field in the API. Without that value, we cannot submit.
        if (!neededDate) {
            Logger.info('[sendReservationToRapidstor] Missing neededDate', location);
            return undefined;
        }

        const isEnquiry = enquiry;
        const sendWebhookOnly = isShowPrices
            ? !location.rapidstor.showPricesBehaviour.sendEmail
        && !location.rapidstor.showPricesBehaviour.createLead
            : false;
        const isSendEmail = isShowPrices ? location.rapidstor.showPricesBehaviour.sendEmail : true;
        // A paid reservation requires the fee to be collected before the email is sent.
        // Signal to the API to suppress the initial email — it will be sent after add-fee succeeds.
        const pendingPayment = isEnquiry && !isShowPrices && !isFreeReservationCheck(location, neededDate);

        Logger.info(
            `[sendReservationToRapidstor] POST ${
                isEnquiry ? 'Lead' : 'Reservation'
            } request`,
            location,
        );
        let dealId = null;
        if (contactCardState.isDeal) {
            dealId = contactCardState.deal || reservationData?.dealObject?._id;
        }

        const externalData = generateExternalData();

        const payload: RapidStorReservationRequest = {
            locationCode: location.locationCode,
            payload: {
                customListing: true,
                deal: dealId,
                sourceUrl: window.location.href,
                enquiry: isEnquiry,
                neededDate: toNeededDate(neededDate),
                // Convert from hour to minute offset
                timezoneOffset: location.timezoneOffset * -60,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                insuranceCoverageId: reservationData?.descriptor.defaultInsuranceCoverage,
                tenant: {
                    tenantId: null,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                },
                unitType: contactCardState.unitType._id,
                webhookOnly: sendWebhookOnly,
                pendingPayment,
                // Tells the API which journey the customer is on. Without it a reserve-only
                // booking looks like an abandoned move-in and, on sites that schedule
                // follow-up emails, its reservation email is held back instead of sent.
                ...(reserveButton && { selectedReserveButton: reserveButton }),
                ...(externalData && { externalData }),
                googleClickId: externalData?.gclid ?? undefined,
            },
        };

        // Respect the `sendEmail` flag from the location config. This is really only false if the triggering action
        // is "show prices" and the location config has `sendEmail` set to false. In that case, we don't want to send an email for the lead/reservation.
        if (!isSendEmail) {
            payload.payload.sendEmail = false;
        }

        if (persona) {
            payload.payload.persona = persona;
        }

        let result;
        if (reservationData?.descriptor) {
            result = await handleMakeReservation(
                payload,
                reservationData.descriptor._id,
            );
            if (!result) {
                throw new Error('Failed to make reservation');
            }
            store.dispatch(
                setReservationData({
                    unitType: contactCardState.unitType,
                    descriptor: reservationData?.descriptor,
                    token: result.token,
                }),
            );
        }

        Logger.info(
            `[sendReservationToRapidstor] ${
                isEnquiry ? 'Lead' : 'Reservation'
            } Created`,
            location,
        );

        return result;
    };

    const sendQuoteUnlockTrackingEvent = () => {
        trackEvent('email_user_quoted', {});
    };

    const fadeOutAndClose = () => {
        if (error) {
            setError(false);
        }
        store.dispatch(
            setContactCard({
                animate: 'fadeOut',
                type: contactCardState.type,
                unitType: null,
            }),
        );

        setTimeout(() => {
            store.dispatch(
                setContactCard({
                    animate: 'initial',
                    type: contactCardState.type,
                    unitType: null,
                }),
            );
        }, 300);
    };

    const sendEmailQuoteToRapidstor = async () => {
        if (!contactCardState.unitType) {
            Logger.info('[sendEmailQuoteToRapidstor] Missing unitType', location);
            return undefined;
        }

        if (!neededDate) {
            Logger.info('[sendEmailQuoteToRapidstor] Missing neededDate', location);
            return undefined;
        }

        Logger.info(
            '[sendEmailQuoteToRapidstor] POST email quote request',
            location,
        );

        const externalData = generateExternalData();
        const body: RapidStorReservationRequest = {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            payload: {
                customListing: true,
                deal: contactCardState.deal || null,
                sourceUrl: window.location.href,
                enquiry: true,
                neededDate: toNeededDate(neededDate),
                // Convert from hour to minute offset
                timezoneOffset: location.timezoneOffset * -60,
                tenant: {
                    tenantId: null,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                },
                unitType: contactCardState.unitType._id,
                webhookOnly: false,
            },
        };

        if (externalData) {
            body.payload.externalData = externalData;
        }

        const result = await emailQuote(body).unwrap();

        Logger.info('[sendEmailQuoteToRapidstor] Email Quote Sent', location);
        return result;
    };

    const handleShowPricesSubmit = async () => {
        const payload: ShowPricesPayload = {
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone,
            email: form.email,
            neededDate: location.rapidstor.showPricesShowNeeded ? neededDate : null,
            locationCode: location.locationCode,
            locationName: location.name,
            description: `${contactCardState.unitType?.width}x${contactCardState.unitType?.length} ${contactCardState.unitType?.typeName}`,
            selected_unit_size: `${contactCardState.unitType?.width}x${contactCardState.unitType?.length} ${location.rapidstor.areaSizeSymbol}`,
        };
        if (contactCardState.unitType) {
            payload.description = `${descriptorDimensions(contactCardState.unitType, location, { parens: true, showDimensionType: true })} ${contactCardState.unitType.typeName}`;
            payload.weekly_price = monthlyToWeekly(
                contactCardState.unitType.rates.discounted
                    ? contactCardState.unitType.rates.discountedRate
                    : contactCardState.unitType.rates.rate,
            );
            payload.discount = contactCardState.unitType.deal?.title || '';
            payload.unitType = {
                dcLength: contactCardState.unitType.length,
                dcPreferredRate: 0,
                dcWidth: contactCardState.unitType.width,
                hasAvailableUnits: contactCardState.unitType.totalAvailable > 0,
                hasComputedAvailable: contactCardState.unitType.hasComputedAvailable,
                iFloor: contactCardState.unitType.floor,
                sLocationCode: contactCardState.unitType.locationCode,
                sTypeName: contactCardState.unitType.typeName,
                unitTypeId: contactCardState.unitType._id,
            };
        }
        if (contactCardState.isDeal && contactCardState.unitType?.deal) {
            payload.deal = {
                ...contactCardState.unitType.deal,
                ConcessionID: contactCardState.unitType?.deal?.discountPlan?.ConcessionID ?? null,
            };
        }
        showPricesEvent(payload);
        store.dispatch(
            setContactData({
                firstName: form.firstName,
                lastName: form.lastName,
                phone: form.phone,
                email: form.email,
                neededDate: location.rapidstor.showPricesShowNeeded
                    ? neededDate.toISOString()
                    : undefined,
            }),
        );
        setCookie(
            'contactData',
            {
                ...form,
                neededDate: location.rapidstor.showPricesShowNeeded
                    ? neededDate.toISOString()
                    : undefined,
            },
            {
                path: window.location.pathname,
                maxAge: 60 * 60 * 24,
            },
        );
        sendQuoteUnlockTrackingEvent();
        try {
            const result = await sendReservationToRapidstor({ enquiry: true, isShowPrices: true });
            if (result?.token && !reservationData?.token && contactCardState.unitType && reservationData?.descriptor) {
                store.dispatch(
                    setReservationData({
                        ...reservationData,
                        token: result.token,
                    }),
                );
            }
        } catch (err) {
            Logger.error(
                `[handleShowPricesSubmit] Error submitting enquiry form: ${JSON.stringify(
                    err,
                )}`,
                location,
            );
            fadeOutAndClose();
            setFormLoading(false);
        }
        store.dispatch(
            setContactCard({
                animate: 'initial',
                type: 'contactMe',
                unitType: null,
                isDeal: false,
                deal: null,
            }),
        );
        setFormLoading(false);
        navigate('/book-unit');
    };

    const convertReservationToEventPayload = (
        reservation: BaseReservation,
    ): ReservationEventPayloadStandard => {
        const { unitType } = reservation;

        return {
            deal_name: contactCardState.unitType?.deal?.title || null,
            quoted_rate_monthly: reservation.rates.rate,
            quoted_rate_weekly: monthlyToWeekly(reservation.rates.rate),
            reservation,
            site_info: {
                location_code: location.locationCode,
                site_token: location.token,
            },
            size: unitType.area,
            tenant: {
                tenantId: reservation.tenantId,
                firstName: reservation.firstName,
                lastName: reservation.lastName,
                email: reservation.email,
                phone: reservation.phone,
            },
            transaction_id: reservation.token,
            unit_id: unitType.unitToUse
                ? unitType.unitToUse.UnitID
                : (null as never),
            unit_length: unitType.length,
            unit_name: reservation.sUnitName,
            unit_rate: reservation.rates.rate,
            unit_width: unitType.width,
            value: reservation.rates.rate,
        };
    };

    const handleReservationSubmit = async () => {
        setFormLoading(true);
        let result: BaseReservation | undefined | null;

        try {
            const isFreeReservation = isFreeReservationCheck(location, neededDate);

            Logger.info(
                `[handleReservationSubmit] Reservation date is ${
                    isFreeReservation ? 'before' : 'not before'
                } the limit, ${isFreeReservation ? 'free' : 'paid'} reservation`,
                location,
            );
            firebaseLogEvent(
                isFreeReservation
                    ? 'free_reservation_created'
                    : 'paid_reservation_created',
                {},
            );

            if (reservationData?.token) {
                result = await patchExistingReservation();
            } else {
                result = await sendReservationToRapidstor({
                    enquiry: !isFreeReservation,
                    reserveButton: 'PayLater',
                });
            }

            if (result) {
                if (isFreeReservation) {
                    const eventPayload = convertReservationToEventPayload(
                        result,
                    );
                    setSubmitted(true);
                    reservationEvent(eventPayload);
                } else {
                    fadeOutAndClose();
                    navigate(`/reservation-with-fee/${result.token}`);
                }
            } else {
                setError(true);
            }
        } catch (err) {
            Logger.error(
                `[handleReservationSubmit] Error submitting reservation form: ${JSON.stringify(
                    err,
                )}`,
                location,
            );
            setError(true);
            fadeOutAndClose();
        } finally {
            setFormLoading(false);
        }
    };

    const handleContactSubmit = async () => {
        let result: BaseReservation | undefined;
        try {
            result = await sendEmailQuoteToRapidstor();
            Logger.info('[handleContactSubmit] Save Quote Email Sent', location);
            setSubmitted(true);
            setFormLoading(false);
            if (result) {
                saveQuoteEvent(convertReservationToEventPayload(result));
                // Book Now patches this reservation with the date stored here. With nothing
                // stored it used to send today, which a site that opens later cannot book.
                const savedNeededDate = neededDate.toISOString();
                store.dispatch(
                    setContactData({
                        firstName: form.firstName,
                        lastName: form.lastName,
                        phone: form.phone,
                        email: form.email,
                        neededDate: savedNeededDate,
                    }),
                );
                setCookie(
                    'contactData',
                    { ...form, neededDate: savedNeededDate },
                    {
                        path: window.location.pathname,
                        maxAge: 60 * 60 * 24,
                    },
                );
                if (result.token && !reservationData?.token && contactCardState.unitType && reservationData?.descriptor) {
                    store.dispatch(
                        setReservationData({
                            ...reservationData,
                            token: result.token,
                        }),
                    );
                }
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            Logger.error(
                `[handleContactSubmit] Error submitting quote email form: ${JSON.stringify(
                    err,
                )}`,
                location,
            );
            setFormLoading(false);
            setError(err?.data?.error || err.message || JSON.stringify(err));
        }
    };

    /**
     * The operator email carries the customer's name but not their phone or
     * email — the controller strips both as structured fields — so the reply
     * details have to travel in the message body.
     *
     * Sent as plain text, so these are real line breaks.
     */
    const largerUnitMessage = () => {
        const lines = [t('contact_card:largerUnit:message')];
        const { unitType } = contactCardState;

        if (unitType) {
            const size = descriptorDimensions(unitType, location, {
                showDimensionType: true,
            });
            const unit = [size, unitType.typeName].filter(Boolean).join(' ');
            lines.push(`${t('contact_card:largerUnit:largest_size')}: ${unit}`);
        }

        lines.push(`${t('contact_card:phone_number')}: ${form.phone}`);
        lines.push(`${t('contact_card:email_address')}: ${form.email}`);

        // The picker only renders for this form when the site turns it on.
        // Without it the date is a default the customer never chose.
        if (location.rapidstor.showPricesShowNeeded && neededDate) {
            const date = moment(neededDate).format(
                location.rapidstor.dateFormat.toUpperCase(),
            );
            lines.push(`${t('contact_card:move_in_date')}: ${date}`);
        }

        return lines.join('\n');
    };

    const handleLargerUnitSubmit = async () => {
        try {
            const result = await postContact({
                locationCode: location.locationCode,
                corpCode: location.corpCode,
                payload: {
                    first_name: form.firstName,
                    last_name: form.lastName,
                    from_email: form.email,
                    phone: form.phone,
                    message: largerUnitMessage(),
                },
            }).unwrap();

            // The route answers 200 with `sent: false` when the send itself fails.
            if (!result?.sent) {
                throw new Error('Contact request was not sent');
            }

            Logger.info('[handleLargerUnitSubmit] Contact request sent', location);
            setSubmitted(true);
        } catch (err) {
            Logger.error(
                `[handleLargerUnitSubmit] Error submitting contact request: ${JSON.stringify(
                    err,
                )}`,
                location,
            );
            setError(true);
        } finally {
            setFormLoading(false);
        }
    };

    const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Native HTML5 required-field validation is disabled (see `noValidate` on
        // the form below) - some mobile browsers mishandle interactive validation
        // when a required field can't be focused (the move-in date picker blurs
        // itself on focus), letting the raw form submission through and reloading
        // the page. This check replaces it.
        if (!form.firstName?.trim() || !form.lastName?.trim() || !form.phone?.trim() || !form.email?.trim()) {
            setRequiredFieldsError(true);
            return;
        }
        setRequiredFieldsError(false);
        // noValidate above also drops the browser's native type="email" format check,
        // so malformed addresses (e.g. "john@", "@gmail.com") need an explicit check here -
        // isValidTld only checks the domain suffix, not overall shape.
        if (!validateEmail(form.email)) {
            setEmailTldError(true);
            return;
        }
        if (emailTldError || !isValidTld(form.email)) {
            setEmailTldError(true);
            return;
        }
        // The larger-unit enquiry sends a contact request rather than a
        // reservation, so it never carries a unitType and must route before the
        // guard below.
        if (contactCardState.type === 'largerUnit') {
            setFormLoading(true);
            firebaseLogEvent('larger_unit_enquiry', {});
            handleLargerUnitSubmit();
            return;
        }
        if (!contactCardState.unitType) {
            if (contactCardState.type !== 'contactMe') {
                Logger.info('[submitForm] Missing unitType', location);
                setFormLoading(false);
                return;
            }
            setFormLoading(true);
            firebaseLogEvent('save_quote', {});
            handleContactSubmit();
            return;
        }
        if (contactCardState.type === 'showPrices') {
            setFormLoading(true);
            firebaseLogEvent('show_prices', {});
            handleShowPricesSubmit();
        } else if (contactCardState.type === 'reservation') {
            setFormLoading(true);
            handleReservationSubmit();
        } else {
            setFormLoading(true);
            firebaseLogEvent('save_quote', {});
            handleContactSubmit();
        }
    };

    return (
        <div
            id="contact-card-modal"
            style={{
                zIndex: 50000,
                position: 'fixed',
            }}
        >
            <motion.div
                layout
                variants={contactVariants[0]}
                initial="initial"
                animate={contactCardState.animate}
                transition={{
                    duration: ANIMATION_SPEED,
                }}
                className={classes.backdrop}
            >
                <motion.div
                    layout
                    variants={contactVariants[1]}
                    initial="initial"
                    animate={contactCardState.animate}
                    transition={{
                        duration: ANIMATION_SPEED,
                    }}
                    className={classes.card}
                >
                    <div
                        className={classes.header}
                        style={{
                            display: contactCardState.animate === 'initial' ? 'none' : 'flex',
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                setSubmitted(false);
                                fadeOutAndClose();
                            }}
                            className={classes.back_btn}
                        >
                            <ChevronLeft />
                        </button>
                        <span className={`${classes.cta}`} data-i18n-key={CTA_TITLE.i18nKey} data-rs-setting-dynamic={CTA_TITLE.setting}>{CTA_TITLE.text}</span>

                        <button
                            type="button"
                            onClick={() => {
                                setSubmitted(false);
                                fadeOutAndClose();
                            }}
                            className={classes.cancel_btn}
                        >
                            {t('cta:cancel')}
                        </button>
                    </div>

                    <div className={`${classes.confirmation} ${!error && classes.hide}`}>
                        <span className={`${classes.header}`}>
                            {t('contact_card:error')}
                        </span>
                        {error && typeof error === 'string' && <p>{error}</p>}
                        {error && typeof error !== 'string' && (
                            <p>{t('contact_card:error_text')}</p>
                        )}
                        <p>
                            {t('contact_card:phone')}
                            :
                            {location.contactPhone}
                        </p>
                        <p>
                            {t('contact_card:email')}
                            :
                            {' '}
                            {location.corpCode === 'CRUK'
                                ? t('contact_card:emailOverride')
                                : location.contactEmail}
                        </p>
                        <Button
                            onclick={() => {
                                setSubmitted(false);
                                fadeOutAndClose();
                            }}
                            type="primary"
                            size="medium"
                        >
                            {t('cta:close')}
                        </Button>
                    </div>

                    <div
                        className={`${classes.copy}  ${
                            (submitted || error) && classes.hide
                        }`}
                        style={{
                            display:
                contactCardState.animate === 'initial' ? 'none' : 'block',
                        }}
                    >
                        <div className={classes.top}>
                            <span className={classes.detail_1} data-i18n-key={detail1.i18nKey}>{detail1.text}</span>
                        </div>
                        <p className={classes.detail_2} data-i18n-key={detail2.i18nKey}>{detail2.text}</p>
                    </div>

                    <div
                        className={`${classes.confirmation}  ${!submitted && classes.hide}`}
                        style={{
                            display:
                contactCardState.animate === 'initial' ? 'none' : 'block',
                        }}
                    >
                        {(() => {
                            if (contactCardState.type === 'reservation' && submitted) {
                                const unpaidReservationRedirectUrl = location.rapidstor.jblRedirects.afterUnpaidReservation.url;
                                const useUnpaidReservationRedirect = location.rapidstor.jblRedirects.afterUnpaidReservation
                                    .enabled;

                                if (
                                    useUnpaidReservationRedirect
                                        && unpaidReservationRedirectUrl
                                ) {
                                    window.location.replace(unpaidReservationRedirectUrl);
                                }
                            }

                            return (
                                <div className={`${classes.container}`}>
                                    <IconMailSent width={48} />
                                    <span className={`${classes.success_1}`} data-i18n-key={success1.i18nKey}>{success1.text}</span>
                                    <p className={`${classes.success_2}`} data-i18n-key={success2.i18nKey}>{success2.text}</p>
                                </div>
                            );
                        })()}

                        <Button
                            onclick={() => {
                                setSubmitted(false);
                                fadeOutAndClose();
                            }}
                            type="primary"
                            size="medium"
                        >
                            {t('cta:close')}
                        </Button>
                    </div>

                    <form
                        className={`${classes.form} ${
                            (submitted || error) && classes.hide
                        }`}
                        onSubmit={submitForm}
                        noValidate
                        style={{
                            display:
                contactCardState.animate === 'initial' ? 'none' : 'block',
                        }}
                    >
                        <div className={classes.name_row}>
                            <div className={classes.part}>
                                <label className={classes.label}>
                                    {t('contact_card:first_name')}
                                </label>
                                <input
                                    type="text"
                                    autoComplete="fName"
                                    name="firstName"
                                    value={form.firstName}
                                    onChange={(e) => update('firstName', e)}
                                    disabled={formLoading}
                                    required
                                    className={classes.input}
                                />
                            </div>
                            <div className={classes.part}>
                                <label className={classes.label}>
                                    {t('contact_card:last_name')}
                                </label>
                                <input
                                    type="text"
                                    autoComplete="lName"
                                    name="lastName"
                                    value={form.lastName}
                                    onChange={(e) => update('lastName', e)}
                                    disabled={formLoading}
                                    required
                                    className={classes.input}
                                />
                            </div>
                        </div>
                        {window.rapidstor_settings.rapidstor_options.dialCodeSelector ? (
                            <div className={classes.part}>
                                <label className={classes.label}>{t('contact_card:phone_number')}</label>
                                <PhoneInput
                                    defaultCountry={defaultCountry as CountryIso2}
                                    value={form.phone}
                                    onChange={(phone) => {
                                        updateForm((draft) => {
                                            draft.phone = phone;
                                            return draft;
                                        });
                                    }}
                                    disabled={formLoading}
                                    required
                                />
                            </div>
                        ) : (
                            <div className={classes.part}>
                                <label className={classes.label}>{t('contact_card:phone_number')}</label>
                                <input
                                    type="tel"
                                    autoComplete="tel"
                                    name="phone"
                                    value={form.phone}
                                    onChange={(e) => update('phone', e)}
                                    disabled={formLoading}
                                    className={classes.input}
                                    required
                                />
                            </div>
                        )}
                        <div className={classes.part}>
                            <label className={classes.label}>{t('contact_card:email_address')}</label>
                            <input
                                type="email"
                                autoComplete="off"
                                name="email"
                                value={form.email}
                                onChange={handleEmailChange}
                                disabled={formLoading}
                                required
                                className={`${classes.input} ${emailTldError ? classes.input_error : ''}`}
                            />
                            {showEmailPills && (
                                <div className={classes.email_pills}>
                                    {EMAIL_PILL_DOMAINS.map((domain) => (
                                        <button
                                            key={domain}
                                            type="button"
                                            className={classes.email_pill}
                                            onClick={() => handlePillClick(domain)}
                                        >
                                            @
                                            {domain}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {emailTldError && (
                                <span className={classes.email_error_text}>
                                    {t('contact_card:invalid_email_domain', 'Please enter a valid email domain')}
                                </span>
                            )}
                        </div>
                        {(location.rapidstor.showPricesShowNeeded
                            || contactCardState.type === 'reservation') && (
                            <div className={`${classes.part}`}>
                                <label className={classes.label}>
                                    {t('contact_card:move_in_date')}
                                </label>
                                <DatePicker
                                    showIcon
                                    icon={<IconCalendar />}
                                    toggleCalendarOnIconClick
                                    selected={neededDate}
                                    minDate={dateRange?.min}
                                    maxDate={dateRange?.max}
                                    required
                                    onFocus={(e) => {
                                        e.currentTarget.blur();
                                    }}
                                    excludeDates={location.rapidstor.datesClosed
                                        .split(',')
                                        .map((d) => moment(d, 'DD-MM-YYYY').toDate())}
                                    disabledKeyboardNavigation
                                    dateFormat={location.rapidstor.dateFormat}
                                    onChange={(e: Date | null) => {
                                        if (e) {
                                            setNeededDate(e);
                                        }
                                    }}
                                    className={`${classes.datepicker} ${classes.input}`}
                                    disabled={formLoading}
                                />
                            </div>
                        )}
                        <div className={classes.part}>
                            {requiredFieldsError && (
                                <span role="alert" className={classes.email_error_text}>
                                    {t('contact_card:required_fields', 'Please fill in all required fields')}
                                </span>
                            )}
                            <Button
                                disabled={formLoading}
                                type="primary"
                                size="medium"
                                data-i18n-key={CTA_BUTTON.i18nKey}
                                data-rs-setting-dynamic={CTA_BUTTON.setting}
                            >
                                {formLoading ? <Spinner /> : CTA_BUTTON.text}
                            </Button>
                        </div>
                        {(isSafeUrl(location.rapidstor.termsAndConditionsUrl) || isSafeUrl(location.rapidstor.privacyPolicyUrl)) && (
                            <div className={classes.policy_links}>
                                {isSafeUrl(location.rapidstor.termsAndConditionsUrl) && (
                                    <a
                                        href={location.rapidstor.termsAndConditionsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.policy_link}
                                    >
                                        {t('contact_card:terms_and_conditions')}
                                    </a>
                                )}
                                {isSafeUrl(location.rapidstor.privacyPolicyUrl) && (
                                    <a
                                        href={location.rapidstor.privacyPolicyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.policy_link}
                                    >
                                        {t('contact_card:privacy_policy')}
                                    </a>
                                )}
                            </div>
                        )}
                    </form>
                </motion.div>
            </motion.div>
        </div>
    );
}
